import { useState, useRef } from "react";
import { FONT } from "./theme";

// Shared Apple/iOS-style hover tooltip for the Phase 1 design-system
// foundation (see linear-brewing-toast.md). The app currently has zero
// custom tooltips — every hover hint is a native browser title= attribute
// (119 of them in OutlookWorkspace.js alone), which means no styling
// control, an ugly delay/appearance the browser controls, and no way to
// show anything richer than plain text. This component is the shared
// primitive future surfaces adopt; migrating each existing title= call site
// onto it is a separate, later pass (Phase 4 in the plan) — not part of
// this change, so nothing currently working is touched.
//
// Usage:
//   <Tooltip label="Send Campaign">
//     <button onClick={...}><RIcon name="send"/></button>
//   </Tooltip>
//
// Wraps its child in an inline-flex span — does not change the child's own
// layout, sizing, or behavior. Shows after a short hover delay (matching
// macOS/iOS's own tooltip timing) so it doesn't flash on quick mouse-overs.
const SIDE_STYLES = {
  top:    { bottom: "calc(100% + 7px)", left: "50%", transform: "translateX(-50%)" },
  bottom: { top: "calc(100% + 7px)", left: "50%", transform: "translateX(-50%)" },
  left:   { right: "calc(100% + 7px)", top: "50%", transform: "translateY(-50%)" },
  right:  { left: "calc(100% + 7px)", top: "50%", transform: "translateY(-50%)" },
};

export function Tooltip({ label, children, side = "top", delay = 450, disabled }) {
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);

  const open = () => {
    if (disabled || !label) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setShow(true), delay);
  };
  const close = () => {
    clearTimeout(timerRef.current);
    setShow(false);
  };

  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
    >
      {children}
      {label && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            ...SIDE_STYLES[side],
            background: "rgba(29,29,31,.94)",
            color: "#fff",
            fontFamily: FONT,
            fontSize: 11.5,
            fontWeight: 500,
            lineHeight: 1.3,
            padding: "5px 9px",
            borderRadius: 7,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            zIndex: 100000,
            boxShadow: "0 4px 14px rgba(0,0,0,.18)",
            opacity: show ? 1 : 0,
            transition: show ? "opacity .12s ease .05s" : "opacity .08s ease",
          }}
        >
          {label}
        </span>
      )}
    </span>
  );
}

export default Tooltip;
